import { useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import {
  GetCmpEventRegistrationsDocument,
  GetCmpEventRegistrationsQuery,
  GetCmpEventRegistrationsQueryVariables,
  GetCmpEventSponsorshipRequestsDocument,
  GetCmpEventSponsorshipRequestsQuery,
  GetCmpEventSponsorshipRequestsQueryVariables,
} from "../../../../graphql/types";
import { eventSalesCallerHandledApolloContext } from "./eventSalesApolloErrorPolicy";

type EventSalesReadbackInput = Readonly<{ chamberId: string; eventId: string }>;

/**
 * Reads the event sales lists again after a confirmed save. A plain query
 * refetch has no caller context, so a failed read sends staff to /error500
 * after the invoice or payment was already saved (UX audit EVT-1, found live
 * on Sand Bay). This read keeps the failure for the caller, which reports
 * "saved, but the list could not refresh". The network-only result also
 * updates the page's list queries through the Apollo cache.
 */
export const useEventSalesReadback = () => {
  const client = useApolloClient();

  const readRegistrations = useCallback(
    (input: EventSalesReadbackInput) =>
      client.query<
        GetCmpEventRegistrationsQuery,
        GetCmpEventRegistrationsQueryVariables
      >({
        context: eventSalesCallerHandledApolloContext,
        fetchPolicy: "network-only",
        query: GetCmpEventRegistrationsDocument,
        variables: { input },
      }),
    [client],
  );

  const readSponsorships = useCallback(
    (input: EventSalesReadbackInput) =>
      client.query<
        GetCmpEventSponsorshipRequestsQuery,
        GetCmpEventSponsorshipRequestsQueryVariables
      >({
        context: eventSalesCallerHandledApolloContext,
        fetchPolicy: "network-only",
        query: GetCmpEventSponsorshipRequestsDocument,
        variables: { input },
      }),
    [client],
  );

  return { readRegistrations, readSponsorships };
};
